import { ontologyFieldDefinitionSchema } from 'features/dynamicOntology'
import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import get from 'lodash/get'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'
import { z } from 'zod'

export type ConditionalFormConfig = z.infer<typeof ConditionalFormConfigSchema>

const CONDITION_OPS = [
  '=',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  'in',
  'not_in',
  'is_null',
  'is_not_null',
  'is_truthy',
  'is_falsy',
] as const

// Zod Schemas
const OntologyFieldDefinitionSchema = ontologyFieldDefinitionSchema.extend({
  defaultValue: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.array(z.number()),
      z.array(z.boolean()),
    ])
    .optional(),
})

const ValueLookupConfigSchema = z.object({
  // Tags to take from the ticket to use as query tags to find lookup entries
  lookupTags: z.array(z.string()).default([]),
  // Type of the lookup entries to find
  lookupType: z.string(),
  // Path in the lookupNameType to retrieve a label from - used as the title in the value list
  lookupNamePath: z.string().optional(),
  // Type of the entry to use as the name for the lookup entries
  lookupNameType: z.string().optional(),
  // Record of form field to list of source field paths to retrieve the values from
  fields: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
})

export type ValueLookupConfig = z.infer<typeof ValueLookupConfigSchema>

const OntologyFieldSchema = z.string()

const ConditionOpSchema = z.enum(CONDITION_OPS)

const ConditionSchema = z.object({
  field: OntologyFieldSchema,
  op: ConditionOpSchema,
  value: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      z.array(z.number()),
      z.array(z.boolean()),
      z.undefined(),
    ])
    .optional(),
})

export type ValueLookupValueType = {
  sourceName: string
  sourceId: string
  nameEntry: GeckoEntry
  field: string
  value:
    | string
    | number
    | boolean
    | ReadonlyArray<string>
    | ReadonlyArray<number>
    | ReadonlyArray<boolean>
}

export function createLookupValues(
  entries: ReadonlyArray<GeckoEntry>,
  lookupType: string,
  lookupNameType: RolodexEntryType | undefined,
  lookupNamePath: string | undefined,
  fields: ValueLookupConfig['fields']
): ReadonlyArray<ValueLookupValueType> {
  const nameRecs = entries.filter((entry) =>
    lookupNameType ? entry.type === lookupNameType : entry.type === lookupType
  )
  const nameRecsMap = new Map(nameRecs.map((entry) => [entry.id, entry]))
  const lookupRecs = entries.filter((entry) => entry.type === lookupType)

  return lookupRecs.flatMap((entry) => {
    const nameRecId =
      lookupNameType && lookupNameType !== lookupType
        ? entry.tags[lookupNameType as RolodexEntryType]
        : entry.id
    const nameRec = nameRecsMap.get(nameRecId ?? entry.id)
    const sourceName =
      nameRec && lookupNamePath
        ? (get(nameRec.data, lookupNamePath) ?? entry.id)
        : entry.id

    return Object.entries(fields)
      .flatMap(([formField, paramObjectField]) => {
        const normObjectField = Array.isArray(paramObjectField)
          ? paramObjectField
          : [paramObjectField]

        return normObjectField.map((objectField) => {
          const objectFieldValue = get(entry.data ?? {}, objectField)
          return objectFieldValue !== undefined && objectFieldValue !== null
            ? {
                sourceName,
                sourceId: entry.id,
                field: formField,
                value: objectFieldValue as ValueLookupValueType['value'],
                nameEntry: nameRec ?? entry,
              }
            : undefined
        })
      })
      .filter((value): value is ValueLookupValueType => value !== undefined)
  })
}

function isTagsEmpty(tags: RolodexEntryQueryBody['tags']): boolean {
  const tagsEntries = Object.entries(tags ?? {})
  return (
    tagsEntries.length === 0 ||
    tagsEntries.every(
      ([, values]) =>
        values === undefined || (Array.isArray(values) && values.length === 0)
    )
  )
}

export function useValueLookups(config: ValueLookupConfig | undefined): {
  values: ReadonlyArray<ValueLookupValueType>
  isLoading: boolean
} {
  const { lookupTags, lookupType, lookupNameType, lookupNamePath, fields } =
    config ?? {
      lookupTags: [],
      lookupType: '',
      lookupNameType: undefined,
      lookupNamePath: undefined,
      fields: {},
    }
  const { ticketTags } = useTicketUpdate()
  const queryTags = useMemo(() => {
    return lookupTags.reduce(
      (acc, tagType) => {
        const ticketTagVal = ticketTags[tagType as RolodexEntryType]
        if (ticketTagVal) {
          return { ...acc, [tagType]: ticketTagVal }
        }
        return acc
      },
      {} as Record<RolodexEntryType, string>
    )
  }, [lookupTags, ticketTags])
  const links = useMemo(
    () =>
      lookupNameType && lookupNameType !== lookupType
        ? [lookupNameType as RolodexEntryType]
        : undefined,
    [lookupNameType, lookupType]
  )
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () =>
        !isTagsEmpty(queryTags)
          ? {
              type: lookupType as RolodexEntryType,
              links,
              tags: queryTags,
            }
          : undefined,
      [lookupType, queryTags, links]
    )
  )

  const values = useMemo<ReadonlyArray<ValueLookupValueType>>(
    () =>
      createLookupValues(
        entries,
        lookupType,
        lookupNameType as RolodexEntryType | undefined,
        lookupNamePath,
        fields
      ),
    [entries, fields, lookupNamePath, lookupNameType, lookupType]
  )
  return useMemo(
    () => ({
      values,
      isLoading,
    }),
    [values, isLoading]
  )
}

export type Condition = z.infer<typeof ConditionSchema>

const ConditionalFormSectionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  fields: z.array(OntologyFieldSchema),
})

export type ConditionalFormSection = z.infer<
  typeof ConditionalFormSectionSchema
>

export const ConditionalFormConfigSchema = z
  .object({
    ontologyFields: z.record(
      OntologyFieldSchema,
      OntologyFieldDefinitionSchema
    ),
    sections: z.array(ConditionalFormSectionSchema),
    form: z.array(
      z.object({
        sectionId: z.string(),
        showWhen: z.array(ConditionSchema).default([]),
      })
    ),
    lookups: ValueLookupConfigSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const seenIds = new Map<string, number>()
    data.sections.forEach((section, index) => {
      if (seenIds.has(section.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Section ids must be unique',
          path: ['sections', index, 'id'],
        })
        const firstIndex = seenIds.get(section.id)
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate of this section id appears later',
            path: ['sections', firstIndex, 'id'],
          })
        }
      } else {
        seenIds.set(section.id, index)
      }
    })
  })

export const ConditionalFormTicketPropsSchema = z.object({
  fieldValues: z
    .record(
      OntologyFieldSchema,
      z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.array(z.string()),
        z.array(z.number()),
        z.array(z.boolean()),
      ])
    )
    .default({}),
})

export type ConditionalFormTicketProps = z.infer<
  typeof ConditionalFormTicketPropsSchema
>
